/**
 * Passerelle unique côté navigateur vers la validation serveur des formulaires.
 *
 * Règles :
 *  - aucune donnée personnelle n'est placée dans une URL (tout part en POST) ;
 *  - aucun texte saisi n'est transmis aux outils analytiques ;
 *  - la validation navigateur reste un confort : le serveur revalide tout ;
 *  - anti-spam : champ honeypot + mesure du temps de remplissage.
 */
import { supabase } from '@/integrations/supabase/client';

export type FormType = 'quote' | 'contact' | 'appointment' | 'newsletter' | 'career' | 'tender';

export type SubmitErrorCode =
  | 'validation_failed'
  | 'rate_limited'
  | 'duplicate'
  | 'temporary_failure'
  | 'network';

export interface SubmitResult {
  ok: boolean;
  reference?: string;
  /** Champs refusés par le serveur (nom du champ → code d'erreur). */
  fields?: Record<string, string>;
  code?: SubmitErrorCode;
  /** Données complémentaires retournées par le serveur (lien agenda, ids…). */
  extra?: Record<string, unknown>;
}

const UTM_KEYS = ['source', 'medium', 'campaign', 'term', 'content'] as const;
const UTM_STORAGE_KEY = 'mtb-utm';
const LANDING_STORAGE_KEY = 'mtb-landing';

/** Nom du champ honeypot : plausible pour un robot, invisible pour l'utilisateur. */
export const HONEYPOT_FIELD = 'company_website';

/** Capture les paramètres UTM et la page d'arrivée une seule fois par session. */
export function captureAcquisitionContext(): void {
  if (typeof window === 'undefined') return;
  try {
    const params = new URLSearchParams(window.location.search);
    const utm: Record<string, string> = {};
    for (const key of UTM_KEYS) {
      const value = params.get(`utm_${key}`);
      if (value) utm[key] = value.slice(0, 120);
    }
    if (Object.keys(utm).length && !sessionStorage.getItem(UTM_STORAGE_KEY)) {
      sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(utm));
    }
    if (!sessionStorage.getItem(LANDING_STORAGE_KEY)) {
      sessionStorage.setItem(LANDING_STORAGE_KEY, JSON.stringify({
        path: window.location.pathname.slice(0, 300),
        referrer: (document.referrer || '').slice(0, 500),
      }));
    }
  } catch {
    /* stockage indisponible : on continue sans contexte d'acquisition */
  }
}

function readAcquisitionContext() {
  if (typeof window === 'undefined') return { utm: undefined, landingPath: undefined, referrer: undefined };
  try {
    const utmRaw = sessionStorage.getItem(UTM_STORAGE_KEY);
    const landingRaw = sessionStorage.getItem(LANDING_STORAGE_KEY);
    const landing = landingRaw ? JSON.parse(landingRaw) as { path?: string; referrer?: string } : {};
    return {
      utm: utmRaw ? JSON.parse(utmRaw) as Record<string, string> : undefined,
      landingPath: landing.path,
      referrer: landing.referrer,
    };
  } catch {
    return { utm: undefined, landingPath: undefined, referrer: undefined };
  }
}

export interface SubmitOptions {
  formType: FormType;
  payload: Record<string, unknown>;
  locale?: string;
  consent?: boolean;
  /** Valeur du champ honeypot telle que rendue dans le DOM. */
  honeypot?: string;
  /** Horodatage (performance.now / Date.now) du montage du formulaire. */
  startedAt?: number;
}

export async function submitForm(options: SubmitOptions): Promise<SubmitResult> {
  const { utm, landingPath, referrer } = readAcquisitionContext();
  const body = {
    formType: options.formType,
    locale: options.locale === 'en' ? 'en' : 'fr',
    consent: options.consent ?? false,
    honeypot: options.honeypot ?? '',
    elapsedMs: options.startedAt ? Math.max(0, Math.round(Date.now() - options.startedAt)) : undefined,
    utm,
    landingPath,
    referrer,
    payload: options.payload,
  };

  try {
    const { data, error } = await supabase.functions.invoke('submit-form', { body });

    // Les réponses 4xx arrivent dans `error`; le corps JSON reste exploitable.
    if (error) {
      let parsed: Record<string, unknown> | null = null;
      const ctx = (error as { context?: Response }).context;
      if (ctx && typeof ctx.json === 'function') {
        try { parsed = await ctx.json(); } catch { parsed = null; }
      }
      const code = (parsed?.error as SubmitErrorCode | undefined) ?? 'temporary_failure';
      const fields: Record<string, string> = {};
      const rawFields = parsed?.fields as Array<{ field: string; code: string }> | undefined;
      rawFields?.forEach((f) => { if (f.field) fields[f.field] = f.code; });
      return {
        ok: false,
        code,
        fields: Object.keys(fields).length ? fields : undefined,
        reference: typeof parsed?.reference === 'string' ? parsed.reference : undefined,
      };
    }

    if (data?.ok) {
      const { ok: _ok, reference, ...extra } = data as Record<string, unknown> & { reference?: string };
      return { ok: true, reference, extra };
    }
    return { ok: false, code: (data?.error as SubmitErrorCode) ?? 'temporary_failure' };
  } catch {
    return { ok: false, code: 'network' };
  }
}

/** Messages utilisateur clairs, bilingues, sans détail technique. */
export function submitErrorMessage(code: SubmitErrorCode | undefined, fr: boolean): string {
  switch (code) {
    case 'validation_failed':
      return fr
        ? 'Certains champs sont invalides. Corrigez-les puis renvoyez le formulaire.'
        : 'Some fields are invalid. Please correct them and submit again.';
    case 'rate_limited':
      return fr
        ? 'Trop de demandes envoyées depuis cet appareil. Réessayez dans quelques minutes.'
        : 'Too many submissions from this device. Please try again in a few minutes.';
    case 'duplicate':
      return fr
        ? 'Cette demande a déjà été envoyée. Notre équipe la traite déjà.'
        : 'This request has already been sent. Our team is already handling it.';
    case 'network':
      return fr
        ? 'Connexion interrompue. Vérifiez votre réseau puis réessayez.'
        : 'Connection lost. Check your network and try again.';
    default:
      return fr
        ? 'Erreur temporaire de notre côté. Réessayez dans un instant.'
        : 'Temporary error on our side. Please try again shortly.';
  }
}

/** Traduction des codes de champ renvoyés par le serveur. */
export function fieldErrorMessage(code: string, fr: boolean): string {
  if (code === 'invalid_email') return fr ? 'Adresse e-mail invalide.' : 'Invalid email address.';
  if (code === 'invalid_phone') return fr ? 'Numéro de téléphone invalide.' : 'Invalid phone number.';
  return fr ? 'Champ invalide ou incomplet.' : 'Invalid or incomplete field.';
}
